import buttonArrowIcon from "@assets/images/Frame-91.svg";
import cardPaymentIcon from "@assets/images/solar-card-bold@2x.png";
import bankTransferIcon from "@assets/images/solar-card-bold1.svg";
import mobilePaymentIcon from "@assets/images/jam-phone-f.svg";
import autoDebitIcon from "@assets/images/mdi-credit-card-refresh.svg";
import { Reveal } from "@components/ui";

interface PaymentMethodCardProps {
  icon: string;
  title: string;
  subtitle?: string;
  tag?: string;
}

function PaymentMethodCard({ icon, title, subtitle, tag = "Available" }: PaymentMethodCardProps) {
  return (
    <div className="group w-full h-auto md:h-[225px] rounded-[16px] bg-[#f6f6f9] flex flex-col items-center p-2 md:pt-2 md:px-2 md:pb-[19px] hover:shadow-[0_4px_20px_rgba(0,0,0,0.08)] transition-all duration-300 border border-transparent hover:border-[#e8e9ed]">
      {/* Icon Container */}
      <div className="self-stretch flex-1 bg-[#f6f6f9] group-hover:bg-[#f24d2b]/[0.08] transition-colors duration-300 rounded-[14px] flex items-center justify-center">
        <img
          src={icon}
          alt=""
          className="w-20 h-auto max-h-[80px] object-contain"
          loading="lazy"
        />
      </div>

      {/* Label */}
      <div className="w-full px-2 lg:px-0 lg:w-[260px] text-sm md:text-lg lg:text-[24px] tracking-[-0.04em] text-[#1e1f24] font-albert-sans leading-[1.3] text-center">
        <span>{title}</span>
        {subtitle && (
          <>
            {" — "}
            <span className="text-[14px] md:text-base lg:text-[18px]">{subtitle}</span>
          </>
        )}
      </div>

      {/* Metadata Tag */}
      <span className="mt-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-[#62636c]/50 font-albert-sans">
        {tag}
      </span>
    </div>
  );
}

const paymentMethods: PaymentMethodCardProps[] = [
  {
    icon: cardPaymentIcon,
    title: "Online Card Payment",
    subtitle: "VISA, MasterCard, Apple Pay",
    tag: "Recommended",
  },
  {
    icon: bankTransferIcon,
    title: "Bank Transfer",
    tag: "Available",
  },
  {
    icon: mobilePaymentIcon,
    title: "Mobile App Payment",
    tag: "Available",
  },
  {
    icon: autoDebitIcon,
    title: "Auto-Debit",
    tag: "Available",
  },
];

export function BillInfoSection() {
  return (
    <section className="w-full bg-white overflow-hidden py-10 md:py-14 lg:py-[100px] px-5 md:px-10 lg:px-5 font-albert-sans">
      <div className="w-full max-w-[1368px] mx-auto flex flex-col items-center gap-16 lg:gap-20">
        {/* Understanding Your Gas Bill */}
        <Reveal direction="up">
          <div className="w-full flex flex-col lg:flex-row items-start lg:items-start justify-between gap-8 lg:gap-[214px]">
            <div className="w-full lg:w-[860px] flex flex-col items-start gap-[19px]">
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#122d63]/[0.08] text-[#122d63] text-[13px] md:text-[15px] font-semibold tracking-[-0.02em] uppercase">
                Billing
              </span>
              <h2 className="self-stretch text-[24px] md:text-[40px] lg:text-[60px] tracking-[-0.04em] font-semibold text-[#122d63] font-albert-sans leading-[1.2]">
                Understanding Your Gas Bill
              </h2>
              <p className="self-stretch text-[14px] md:text-base lg:text-[28px] tracking-[-0.04em] text-[#62636c] font-albert-sans leading-[1.4]">
                Each GAZ bill shows: Billing Period and meter reading (start to
                end), Consumption in m³ or Kg, Tariff rate applied, VAT (5%) and
                any adjustments, Due Date and Total Amount Payable
              </p>
            </div>
            <button className="flex items-center gap-2.5 py-3 pl-4 pr-2 bg-[#090d2c] rounded-[40px] text-xs md:text-base lg:text-[24px] tracking-[-0.04em] text-white font-albert-sans hover:bg-[#3d405e] transition-colors shrink-0 whitespace-nowrap">
              <span>Download Sample Bill</span>
              <img
                src={buttonArrowIcon}
                alt=""
                className="w-10 rounded-[100px] max-h-full"
              />
            </button>
          </div>
        </Reveal>

        {/* Divider */}
        <div className="w-full max-w-[1360px] h-px mt-4 bg-gradient-to-r from-transparent via-[#b9bbc6] to-transparent" />

        {/* Multiple Ways to Pay */}
        <div className="w-full flex flex-col items-start gap-10 lg:gap-[49px]">
          <Reveal direction="up">
            <div className="flex flex-col items-start gap-[19px]">
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#122d63]/[0.08] text-[#122d63] text-[13px] md:text-[15px] font-semibold tracking-[-0.02em] uppercase">
                Billing
              </span>
              <h2 className="self-stretch text-[24px] md:text-[40px] lg:text-[60px] tracking-[-0.04em] font-semibold text-[#122d63] font-albert-sans leading-[1.2]">
                Multiple Ways to Pay
              </h2>
            </div>
          </Reveal>

          {/* Payment Method Cards */}
          <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {paymentMethods.map((method, index) => (
              <Reveal key={method.title} direction="up" delay={index * 100}>
                <PaymentMethodCard {...method} />
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
